import React from "react";
import { Box, SimpleGrid, type SpaceProps } from "@chakra-ui/react";
import { FillImage } from "components/images";
import { Section, sectionSpacingY } from "components/section";
import { Card, HorizontalCard, type CardProps } from "ui/cards";
import { TextTitle } from "ui/text";
import { Button } from "ui/button";
import firstCardImage from "../../../../public/assets/solutions/data/card-1.png";
import secondCardImage from "../../../../public/assets/solutions/data/card-2.png";
import thirdCardImage from "../../../../public/assets/solutions/data/card-3.png";

const fillCardProps: Pick<CardProps, "variant" | "maxW" | "textMaxW" | "mediaRatio"> & SpaceProps = {
  variant: "fill",
  maxW: { base: "cardMaxW", md: "100%" },
  textMaxW: "480px",
  mediaRatio: 647 / 535,
  pb: { base: "tiny", lg: "smaller" },
};

export function DataWarehouseStrategy() {
  return (
    <Section my={0} py={sectionSpacingY}>
      <TextTitle align="center" size="lg" title="Put your data warehouse at the center of your data strategy" />
      <Box mt={{ base: "biggest", md: "huge" }}>
        <SimpleGrid justifyItems={{ base: "center", md: "stretch" }} columns={{ base: 1, md: 2 }} gap="small">
          <Card
            {...fillCardProps}
            title="Collect trusted data"
            description="Collect and load events directly into your data warehouse with SDKs for websites, mobile apps, and server-side applications."
            actions={
              <Button hasRightChevron href="/platform/events">
                Learn about Hightouch Events
              </Button>
            }
            media={
              <FillImage
                src={firstCardImage}
                alt="Illustration depicting UI where a user connects to an API. The logic performed displays when a row is changed, run code to connect to an API with specific rate limits and error handling."
              />
            }
          />
          <Card
            {...fillCardProps}
            title="Unify customer profiles"
            description="Create a single source of truth for customer data directly in your warehouse with a visual rule builder."
            actions={
              <Button hasRightChevron href="/platform/identity-resolution">
                Learn about Identity Resolution
              </Button>
            }
            media={
              <FillImage
                src={secondCardImage}
                alt="Abstract illustration showing Hightouch acting as a means to connect a data warehouse to transactional databases and message distribution tools."
              />
            }
          />
        </SimpleGrid>
        <HorizontalCard
          mt="small"
          mx="auto"
          textMaxW="340px"
          title="Sync data anywhere"
          descriptionAs="div"
          description="Activate your data warehouse directly into your business tools. No more custom API integrations or CSV exports."
          actions={
            <Button hasRightChevron href="/platform/reverse-etl">
              Learn about Reverse ETL
            </Button>
          }
          image={{
            src: thirdCardImage,
            alt: "Abstract illustration showing a data warehouse on the left and a variety of destination logos on the right such as Facebook Ads, Slack, Intercom, Google Ads, and more.",
            ratio: 660 / 392,
          }}
        />
      </Box>
    </Section>
  );
}
